import { createServerFn } from "@tanstack/react-start";
import { getRequestHeader } from "@tanstack/react-start/server";
import {
  runSubmitBooking,
  runSubmitContact,
  runSubscribeNewsletter,
  runUnsubscribeNewsletter,
} from "./intake.impl";

// Resolve current user id + verified email from optional bearer token
// (without requiring auth). Returning the email lets us bind the submission
// to the authenticated identity so a signed-in user cannot spoof someone
// else's email on a public form.
async function tryGetAuthContext(): Promise<{
  userId: string | null;
  email: string | null;
}> {
  try {
    const auth = getRequestHeader("authorization");
    if (!auth) return { userId: null, email: null };
    const token = auth.replace(/^Bearer\s+/i, "").trim();
    if (!token) return { userId: null, email: null };
    const { supabaseAdmin } = await import(
      "@/integrations/supabase/client.server"
    );
    const { data } = await supabaseAdmin.auth.getUser(token);
    const user = data.user;
    if (!user) return { userId: null, email: null };
    return {
      userId: user.id,
      email: user.email_confirmed_at ? (user.email ?? null) : null,
    };
  } catch {
    return { userId: null, email: null };
  }
}

function clientKey(): string {
  const xff = getRequestHeader("x-forwarded-for") ?? "";
  const cf = getRequestHeader("cf-connecting-ip") ?? "";
  const real = getRequestHeader("x-real-ip") ?? "";
  const ip = (cf || xff.split(",")[0] || real || "").trim();
  return ip || "unknown";
}

async function enforceLimit(scope: string, key: string) {
  try {
    const { assertRateLimit } = await import("./_rate-limit.server");
    await assertRateLimit({ scope, key, windowMinutes: 10, limit: 5 });
  } catch (e) {
    throw e instanceof Error ? e : new Error("Rate limit exceeded");
  }
}

export const submitBooking = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) => input)
  .handler(async ({ data }) => {
    const { userId, email } = await tryGetAuthContext();
    const ipKey = clientKey();
    await enforceLimit("intake:booking", `${userId ?? "anon"}:${ipKey}`);
    // If authenticated with a verified email, bind the record's email to it.
    const payload =
      email && data && typeof data === "object"
        ? { ...(data as Record<string, unknown>), email }
        : data;
    return runSubmitBooking(payload, userId);
  });

export const submitContact = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) => input)
  .handler(async ({ data }) => {
    const { userId, email } = await tryGetAuthContext();
    const ipKey = clientKey();
    await enforceLimit("intake:contact", `${userId ?? "anon"}:${ipKey}`);
    const payload =
      email && data && typeof data === "object"
        ? { ...(data as Record<string, unknown>), email }
        : data;
    return runSubmitContact(payload, userId);
  });

export const subscribeNewsletter = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) => input)
  .handler(async ({ data }) => {
    await enforceLimit("intake:subscribe", clientKey());
    return runSubscribeNewsletter(data);
  });

export const unsubscribeNewsletter = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) => input)
  .handler(async ({ data }) => runUnsubscribeNewsletter(data));

// Public: returns booked time slots for a given YYYY-MM-DD so the
// /book page can disable already-taken slots in real time.
export const getAvailableSlots = createServerFn({ method: "GET" })
  .inputValidator((input: unknown) => {
    const raw = (input ?? {}) as { date?: string };
    const date = String(raw.date ?? "");
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      throw new Error("Invalid date");
    }
    return { date };
  })
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import(
      "@/integrations/supabase/client.server"
    );
    const { data: rows, error } = await supabaseAdmin
      .from("consultation_bookings")
      .select("preferred_time, status")
      .eq("preferred_date", data.date)
      .not("status", "eq", "cancelled");
    if (error) {
      console.error("[getAvailableSlots]", error);
      return { booked: [] as string[] };
    }
    const booked = Array.from(
      new Set(
        (rows ?? [])
          .map((r) => String(r.preferred_time ?? "").slice(0, 5))
          .filter(Boolean),
      ),
    );
    return { booked };
  });
